<section class="request-detail">
  @if (isLoading()) {
  <div class="state-panel" role="status" aria-live="polite">
    <app-progress-spinner [diameter]="56" />
    <h2 class="state-panel__title">Loading request…</h2>
    <p class="state-panel__message">Hang tight while we fetch this request.</p>
  </div>
  } @else if (loadError()) {
  <div class="state-panel" role="alert">
    <span
      class="state-panel__icon"
      [class.state-panel__icon--error]="!notFound()"
      aria-hidden="true"
    >
      <app-icon
        [name]="errorIcon()"
        size="custom"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
    </span>
    <h1 class="state-panel__title">{{ errorTitle() }}</h1>
    <p class="state-panel__message">{{ loadError() }}</p>
    <a class="primary-button state-panel__button fw-600" routerLink="/requests">
      <app-icon
        name="chevron_left"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Back to Requests</span>
    </a>
  </div>
  } @else {
  <nav class="breadcrumb flex align-center gap-8 fs-12 mb-15" aria-label="Breadcrumb">
    <a
      class="breadcrumb__link fw-600"
      routerLink="/requests"
      aria-label="Return to linking requests queue"
      >Requests</a
    >
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <span aria-current="page">{{ patientNameLabel() }}</span>
  </nav>

  <header class="page-header flex justify-between align-center gap-12">
    <div>
      <h1 id="request-detail-title" class="page-title fs-22 fw-700 m-0">
        {{ patientNameLabel() }}
      </h1>
      <div class="page-subtitle flex align-center gap-8">
        @if (hasSecurityAlert()) {
        <span class="badge badge--alert">
          <app-icon
            name="warning"
            size="custom"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
          <span>Alert</span>
        </span>
        }
        <!-- The staff status stays visible on flagged requests too, so a
             status saved from the Request Status card is never invisible. -->
        <span class="badge" [class]="statusBadgeClass()">{{ currentStatusLabel() }}</span>
        <span>{{ submittedHeader() }}</span>
      </div>
    </div>

    <!-- The button reports that it took the click before the route changes. The search screen is a
         sibling route, so this screen is gone once it activates; this covers the auth guard run in
         between, the stretch where an unchanged button reads as a click that didn't land. -->
    <button
      type="button"
      class="primary-button page-header__search fw-600"
      [disabled]="isOpeningSearch()"
      (click)="goToSearchRecords()"
    >
      @if (isOpeningSearch()) {
      <span class="button-spinner" aria-hidden="true"></span>
      <span>Opening…</span>
      } @else {
      <app-icon
        name="person_search"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Search Patient Records</span>
      }
    </button>
  </header>

  @if (hasSecurityAlert()) {
  <div class="alert-banner flex gap-12" role="alert">
    <app-icon
      class="alert-banner__icon"
      name="warning"
      size="sm"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <!-- The API doesn't report suspension state, so the copy must not assert
         one — it states only what the attempts list actually shows. -->
    <p class="alert-banner__text">
      <strong>Multiple failed attempts detected.</strong>
      This account submitted {{ attempts().length }} incorrect identity verifications. Review
      carefully before taking action — this may indicate an unauthorized access attempt.
    </p>
  </div>
  }

  <div class="detail-layout">
    <div class="detail-main flex flex-col">
      <section class="info-card" aria-labelledby="submitted-info-title">
        <div class="info-card__header flex justify-between align-center gap-8">
          <div class="info-card__title-group flex gap-8">
            <app-icon
              class="info-card__icon"
              name="assignment_ind"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <h2 id="submitted-info-title" class="info-card__title">Submitted Information</h2>
          </div>
          <div class="flex align-center gap-8">
            @if (hasSecurityAlert()) {
            <span class="badge badge--alert">
              <app-icon
                name="warning"
                size="custom"
                color="custom"
                customColor="currentColor"
                role="presentation"
              ></app-icon>
              <span>Alert</span>
            </span>
            }
            <span class="badge" [class]="statusBadgeClass()">{{ currentStatusLabel() }}</span>
          </div>
        </div>

        <div class="info-card__body">
          <dl class="submitted-fields submitted-fields--grid">
            <div class="flex flex-col gap-2">
              <dt>Full Name</dt>
              <dd>{{ patientNameLabel() }}</dd>
            </div>
            <div class="flex flex-col gap-2">
              <dt>Date of Birth</dt>
              <dd>{{ dateOfBirthLabel() }}</dd>
            </div>
            <div class="flex flex-col gap-2">
              <dt>Identifier Type</dt>
              <dd>{{ identifierTypeLabel() }}</dd>
            </div>
            <div class="flex flex-col gap-2">
              <dt>{{ identifierFieldLabel() }}</dt>
              <dd [class.masked]="isSsnIdentifier()" [attr.aria-label]="identifierAriaLabel()">
                {{ identifierValue() }}
              </dd>
            </div>
            <div class="flex flex-col gap-2">
              <dt>Portal Account Email</dt>
              <dd>{{ request().emailAddress }}</dd>
            </div>
            <div class="flex flex-col gap-2">
              <dt>Phone Number</dt>
              <dd>{{ phoneNumberLabel() }}</dd>
            </div>
            <div class="flex flex-col gap-2">
              <dt>Account Type</dt>
              <dd>{{ accountTypeLabel }}</dd>
            </div>
          </dl>
        </div>
      </section>

      @if (adjustSearchOpen()) {
      <app-adjust-search
        [firstName]="adjustFirstName()"
        [lastName]="adjustLastName()"
        [dateOfBirth]="request().dateOfBirth"
        [lastFiveSsn]="request().lastFiveSocialSecurityNumber"
        [mrn]="request().medicalRecordNumber"
        [initialCriteria]="savedAdjustCriteria()"
        (cancelled)="onAdjustSearchCancelled($event)"
        (searchRun)="handleSearchRun($event)"
      ></app-adjust-search>
      } @else {
      <section class="info-card" aria-labelledby="search-results-title">
        <div class="info-card__header flex align-center gap-12">
          <div class="info-card__title-group info-card__title-group--grow flex gap-8">
            <app-icon
              class="info-card__icon"
              name="manage_search"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <h2 id="search-results-title" class="info-card__title">
              Patient Record Search Results
            </h2>
          </div>

          <span class="info-card__meta fs-12">
            {{ isSearching() ? 'Searching for matching patient records...' : searchSummary() }}
          </span>

          <button
            type="button"
            class="ghost-button fw-600"
            (click)="openAdjustSearch()"
            aria-label="Adjust patient record search"
            [disabled]="isSearching()"
          >
            <app-icon
              name="tune"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span>{{ isSearching() ? 'Searching...' : 'Adjust Search' }}</span>
          </button>
        </div>

        <div
          class="info-card__body info-card__body--spaced flex flex-col gap-12"
          [attr.aria-busy]="isSearching()"
        >
          @if (searchError()) {
          <p class="inline-error flex align-center gap-8 fs-12" role="alert">
            <app-icon
              name="info"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span>{{ searchError() }}</span>
          </p>
          }

          <!-- Searches return a fixed page with no paging UI, so say so when the
               server matched more — otherwise staff could deny a request whose
               real match sat past the cut-off. -->
          @if (!isSearching() && isTruncated()) {
          <p class="inline-notice flex align-center gap-8 fs-12" role="status">
            <app-icon
              name="info"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span
              >More records matched than are shown. Use Adjust Search to narrow the criteria before
              deciding this request has no match.</span
            >
          </p>
          } @if (isSearching()) {
          <div class="search-results-loading empty-state" role="status" aria-live="polite">
            <app-progress-spinner [diameter]="40" />
            <h3 class="fs-16">Searching records...</h3>
            <p class="fs-12">Updated patient matches will appear here when the search completes.</p>
          </div>
          } @else if (hasSearchResults()) {
          <!-- Plain list of toggle buttons, not an ARIA listbox: a listbox
               promises single-tab-stop arrow-key navigation, which these
               individually focusable cards don't provide. aria-pressed carries
               the selected state, so each button must keep its implicit button
               role — role="listitem" goes on the wrapper, never on the control
               (a role there would drop the button semantics *and* aria-pressed,
               leaving colour as the only cue for the selected record). -->
          <div class="search-results-list" role="list" aria-label="Patient record matches">
            @for (result of searchResults(); track result.patientId) {
            <div role="listitem">
              <button
                type="button"
                class="search-result-card flex"
                [class.search-result-card--selected]="isSearchResultSelected(result.patientId)"
                [attr.aria-pressed]="isSearchResultSelected(result.patientId)"
                [attr.aria-label]="'Select record for ' + result.displayName"
                (click)="selectSearchResult(result.patientId)"
              >
                <span class="search-result-card__avatar" aria-hidden="true"
                  >{{ getResultInitials(result) }}</span
                >
                <span class="search-result-card__info flex flex-col gap-8">
                  <span
                    class="search-result-card__name fs-14 fw-600"
                    [class.search-result-card__field--mismatch]="isResultNameMismatch(result)"
                  >
                    {{ result.displayName }} @if (isResultNameMismatch(result)) {
                    <app-icon
                      class="search-result-card__mismatch-icon"
                      name="warning"
                      size="custom"
                      color="custom"
                      customColor="currentColor"
                      ariaLabel="Name does not match the submitted information"
                    ></app-icon>
                    }
                  </span>
                  <span class="search-result-card__meta flex flex-wrap fs-12">
                    <span [class.search-result-card__field--mismatch]="isResultDobMismatch(result)">
                      <strong>DOB:</strong> {{ getSearchResultDateOfBirth(result) }} @if
                      (isResultDobMismatch(result)) {
                      <app-icon
                        class="search-result-card__mismatch-icon"
                        name="warning"
                        size="custom"
                        color="custom"
                        customColor="currentColor"
                        ariaLabel="Date of birth does not match the submitted information"
                      ></app-icon>
                      }
                    </span>
                    <span [class.search-result-card__field--mismatch]="isResultMrnMismatch(result)">
                      <strong>MRN:</strong> {{ getSearchResultMedicalRecordNumber(result) }} @if
                      (isResultMrnMismatch(result)) {
                      <app-icon
                        class="search-result-card__mismatch-icon"
                        name="warning"
                        size="custom"
                        color="custom"
                        customColor="currentColor"
                        ariaLabel="MRN does not match the submitted information"
                      ></app-icon>
                      }
                    </span>
                    <span
                      ><strong>PCP:</strong> {{ getSearchResultPrimaryCareProvider(result) }}</span
                    >
                    <span><strong>Address:</strong> {{ getSearchResultAddress(result) }}</span>
                    <span><strong>Phone:</strong> {{ getSearchResultPhoneNumber(result) }}</span>
                  </span>
                </span>
              </button>
            </div>
            }
          </div>

          <p class="guidance-text fs-12">
            Select a record above to link, or use Adjust Search to broaden the criteria. If no match
            exists, you can request more information from the patient.
          </p>
          } @else {
          <div class="empty-state" role="status" aria-live="polite">
            <span class="empty-state__icon" aria-hidden="true">
              <app-icon
                name="manage_search"
                size="custom"
                color="custom"
                customColor="currentColor"
                role="presentation"
              ></app-icon>
            </span>
            <h3 class="empty-state__title">No matching records found</h3>
            <p class="empty-state__message">{{ searchEmptyStateMessage() }}</p>
          </div>
          }
        </div>
      </section>
      } @if (hasSecurityAlert()) {
      <section class="info-card" aria-labelledby="attempt-history-title">
        <div class="info-card__header flex justify-between align-center gap-8">
          <div class="info-card__title-group flex gap-8">
            <app-icon
              class="info-card__icon"
              name="history"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <h2 id="attempt-history-title" class="info-card__title">Attempt History</h2>
          </div>
        </div>

        <div class="info-card__body">
          <div
            class="timeline flex flex-col"
            role="list"
            aria-label="Identity verification attempt history"
          >
            @for (event of attemptTimeline(); track event.id) {
            <div class="timeline__item flex gap-12" role="listitem">
              <div class="timeline__dot" [class.timeline__dot--alert]="event.kind === 'alert'">
                <app-icon
                  [name]="event.icon"
                  size="xs"
                  color="custom"
                  customColor="currentColor"
                  role="presentation"
                ></app-icon>
              </div>
              <div class="timeline__content">
                <p class="timeline__text">{{ event.description }}</p>
                <p class="timeline__time fs-12">{{ event.timestamp }}</p>
              </div>
            </div>
            }
          </div>
        </div>
      </section>
      }

      <section class="info-card" aria-labelledby="activity-title">
        <div class="info-card__header flex justify-between align-center gap-8">
          <div class="info-card__title-group flex gap-8">
            <app-icon
              class="info-card__icon"
              name="history"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <h2 id="activity-title" class="info-card__title">Activity &amp; Notes</h2>
          </div>
        </div>

        <div class="info-card__body">
          <div class="timeline flex flex-col" role="list" aria-label="Request activity timeline">
            @for (event of timeline(); track event.id) {
            <div class="timeline__item flex gap-12" role="listitem">
              <div class="timeline__dot" [class.timeline__dot--staff]="event.kind === 'staff'">
                <app-icon
                  [name]="event.icon"
                  size="xs"
                  color="custom"
                  customColor="currentColor"
                  role="presentation"
                ></app-icon>
              </div>
              <div class="timeline__content">
                <p class="timeline__text">{{ event.description }}</p>
                @if (event.variant === 'note') {
                <p class="timeline__note-bubble">{{ event.noteBody }}</p>
                <p class="timeline__author">
                  <app-icon
                    class="timeline__author-icon"
                    name="account_circle"
                    size="custom"
                    color="custom"
                    customColor="currentColor"
                    role="presentation"
                  ></app-icon>
                  <span>{{ event.authorName }}</span>
                  <span class="timeline__author-sep" aria-hidden="true">·</span>
                  <span>{{ event.date }}</span>
                  <span class="timeline__author-sep" aria-hidden="true">·</span>
                  <span>{{ event.time }}</span>
                </p>
                } @else {
                <p class="timeline__time fs-12">{{ event.timestamp }}</p>
                }
              </div>
            </div>
            }
          </div>
        </div>
      </section>

      <!-- The section's accessible name comes from its own (visually hidden)
           heading; the visible label names only the textarea. -->
      <section class="note-card" aria-labelledby="add-note-title">
        <h3 id="add-note-title" class="sr-only">Add a Note</h3>
        <label class="note-card__title" for="request-note">Add a Note</label>
        <textarea
          id="request-note"
          class="note-input"
          [value]="noteText()"
          (input)="updateNoteText($any($event.target).value)"
          placeholder="Type a note..."
          rows="4"
        ></textarea>

        @if (showNoteError()) {
        <p class="note-card__error flex align-center gap-8" role="alert">
          <app-icon
            name="info"
            size="xs"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
          <span>Type a note before adding it.</span>
        </p>
        }

        <!-- A failed save must never look like a successful one: the note is an
             audit record, and the text stays in the box for a retry. -->
        @if (noteError()) {
        <p class="note-card__error flex align-center gap-8" role="alert">
          <app-icon
            name="info"
            size="xs"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
          <span>{{ noteError() }}</span>
        </p>
        }

        <div class="note-actions flex justify-end">
          <button
            type="button"
            class="ghost-button fw-600"
            (click)="addNote()"
            [disabled]="isSubmittingNote()"
          >
            <app-icon
              name="add_comment"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span>{{ isSubmittingNote() ? 'Adding…' : 'Add Note' }}</span>
          </button>
        </div>
      </section>
    </div>

    <aside class="detail-aside flex flex-col">
      <section class="action-panel" aria-labelledby="actions-title">
        <div class="action-panel__header flex align-center gap-8">
          <app-icon
            class="info-card__icon"
            name="bolt"
            size="sm"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
          <h2 id="actions-title" class="action-panel__title">{{ actionsPanelTitle() }}</h2>
        </div>

        <div class="flex flex-col">
          <button
            type="button"
            class="action-row flex align-center gap-12"
            (click)="openModal('confirm-link')"
            [disabled]="!selectedPatient()"
          >
            <span class="action-row__icon action-row__icon--success">
              <app-icon
                name="link"
                size="sm"
                color="custom"
                customColor="currentColor"
                role="presentation"
              ></app-icon>
            </span>
            <span class="action-row__content flex flex-col gap-2">
              <span class="action-row__heading">Confirm &amp; Link Record</span>
              <span class="action-row__description">{{ confirmActionDescription() }}</span>
            </span>
            <app-icon
              class="action-row__chevron"
              name="chevron_right"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
          </button>

          <button
            type="button"
            class="action-row flex align-center gap-12"
            (click)="openModal('deny-request')"
          >
            <span class="action-row__icon action-row__icon--danger">
              <app-icon
                name="block"
                size="sm"
                color="custom"
                customColor="currentColor"
                role="presentation"
              ></app-icon>
            </span>
            <span class="action-row__content flex flex-col gap-2">
              <span class="action-row__heading">{{ denyActionTitle() }}</span>
              <span class="action-row__description">{{ denyActionDescription() }}</span>
            </span>
            <app-icon
              class="action-row__chevron"
              name="chevron_right"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
          </button>
        </div>
      </section>

      <app-request-status-card
        [requestId]="requestId"
        [version]="request().version"
        [currentStatusTypeId]="request().workflowStatusTypeId ?? null"
        (statusUpdated)="onStatusUpdated($event)"
      ></app-request-status-card>

      <section class="info-card contact-panel" aria-labelledby="contact-patient-title">
        <div class="info-card__header flex align-center gap-8">
          <app-icon
            class="info-card__icon"
            name="phone_in_talk"
            size="sm"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
          <h2 id="contact-patient-title" class="info-card__title">Contact Patient &amp; Verify</h2>
        </div>

        <div class="info-card__body flex flex-col gap-12">
          <dl class="submitted-fields contact-fields flex flex-col gap-10">
            <div class="flex flex-col gap-2">
              <dt>Patient Phone</dt>
              <dd>{{ phoneNumberLabel() }}</dd>
            </div>
            <div class="flex flex-col gap-2">
              <dt>Patient Email</dt>
              <dd>{{ request().emailAddress }}</dd>
            </div>
          </dl>

          <div class="divider divider--tight"></div>

          <label class="note-label contact-panel__label fs-12 fw-600" for="contact-note"
            >Document Conversation</label
          >
          <p class="contact-panel__hint">
            Record the outcome of your conversation with the patient. This will be saved to the
            activity log.
          </p>
          <textarea
            id="contact-note"
            class="note-input"
            [value]="contactNoteText()"
            (input)="updateContactNoteText($any($event.target).value)"
            placeholder="e.g. Called patient and verified identity using the details on file..."
            rows="5"
          ></textarea>

          @if (showContactNoteError()) {
          <p class="contact-panel__error flex align-center gap-8" role="alert">
            <app-icon
              name="info"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span>Add a note describing the conversation before logging.</span>
          </p>
          }

          <!-- A failed save must never look like a successful one: the note is
               an audit record, and the text stays in the box for a retry. -->
          @if (contactSaveError()) {
          <p class="contact-panel__error flex align-center gap-8" role="alert">
            <app-icon
              name="info"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span>{{ contactSaveError() }}</span>
          </p>
          }

          <div class="contact-panel__actions flex justify-end gap-8">
            <button
              type="button"
              class="ghost-button fw-600"
              (click)="cancelContactNote()"
              [disabled]="isLoggingContact() || !contactNoteText()"
            >
              Cancel
            </button>
            <button
              type="button"
              class="success-button contact-panel__save fw-600"
              (click)="logContact()"
              [disabled]="isLoggingContact()"
            >
              <app-icon
                name="save"
                size="xs"
                color="custom"
                customColor="currentColor"
                role="presentation"
              ></app-icon>
              <span>{{ isLoggingContact() ? 'Logging…' : 'Log Contact' }}</span>
            </button>
          </div>
        </div>
      </section>

      <section class="info-card" aria-labelledby="assignment-title">
        <div class="info-card__header flex align-center gap-8">
          <app-icon
            class="info-card__icon"
            name="person"
            size="sm"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
          <h2 id="assignment-title" class="info-card__title">Assignment</h2>
        </div>

        <div class="info-card__body flex flex-col gap-12">
          <dl class="submitted-fields m-0">
            <div class="flex flex-col gap-2">
              <dt>Assigned To</dt>
              <dd [class.assignment-value--unassigned]="!isAssigned()">{{ assignedToLabel() }}</dd>
            </div>
          </dl>

          @if (assignmentError()) {
          <p class="inline-error flex align-center gap-8 fs-12" role="alert">
            <app-icon
              name="info"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span>{{ assignmentError() }}</span>
          </p>
          } @if (isAssignedToMe()) {
          <button
            type="button"
            class="ghost-button assignment-button fw-600"
            (click)="unassign()"
            [disabled]="isAssigning()"
          >
            <app-icon
              name="person_remove"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span>{{ isAssigning() ? 'Unassigning…' : 'Unassign' }}</span>
          </button>
          } @else {
          <button
            type="button"
            class="ghost-button assignment-button fw-600"
            (click)="assignToMe()"
            [disabled]="isAssigning()"
          >
            <app-icon
              name="person_add"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
            <span>{{ isAssigning() ? 'Assigning…' : 'Assign to Me' }}</span>
          </button>
          }
        </div>
      </section>
    </aside>
  </div>
  } @if (activeModal() === 'confirm-link') {
  <div class="modal-backdrop" role="presentation" (click)="closeModal()">
    <app-confirm-request-modal
      [requestId]="requestId"
      [request]="request()"
      [selectedPatient]="selectedPatient()"
      (cancelled)="closeModal()"
      (confirmed)="onRequestConfirmed()"
      (click)="$event.stopPropagation()"
    ></app-confirm-request-modal>
  </div>
  } @if (activeModal() === 'deny-request') {
  <div class="modal-backdrop" role="presentation" (click)="closeModal()">
    <app-deny-request-modal
      [requestId]="requestId"
      [version]="request().version"
      [patientName]="patientNameLabel()"
      (cancelled)="closeModal()"
      (denied)="onRequestDenied()"
      (click)="$event.stopPropagation()"
    ></app-deny-request-modal>
  </div>
  }

  <!-- The live region is rendered unconditionally: a role="status" element
       inserted together with its text is not reliably announced, so the visual
       chip below is hidden from assistive tech and this region carries the
       message instead. Failures raise the assertiveness to role="alert" so an
       error isn't queued behind whatever the reader is already saying. The
       live politeness follows the role: a fixed "polite" would override the
       assertive one an alert implies. -->
  <div
    class="sr-only"
    [attr.role]="toastVariant() === 'error' ? 'alert' : 'status'"
    [attr.aria-live]="toastVariant() === 'error' ? 'assertive' : 'polite'"
  >
    {{ toastMessage() }}
  </div>

  @if (toastMessage()) {
  <div
    class="toast flex align-center gap-8"
    [class.toast--error]="toastVariant() === 'error'"
    aria-hidden="true"
  >
    <app-icon
      class="toast__icon"
      [name]="toastVariant() === 'error' ? 'warning' : 'check_circle'"
      size="sm"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <span class="toast__text">{{ toastMessage() }}</span>
  </div>
  }
</section>
